import { ChevronDown, Paperclip, Send, Sparkles, X } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type DragEvent,
  type FormEvent,
} from "react";
import { MailComposeEditor } from "@/components/MailComposeEditor";
import {
  api,
  humanizeClientError,
  isUnreachableClientError,
  type MailAccount,
} from "@/lib/api";
import type { ComposeAttachment, ComposeDraft } from "@/mail/composeDraft";
import { applyReplyMode } from "@/mail/composeDraft";
import { extractInlineImagesFromHtml } from "@/mail/composeInlineImages";
import {
  compressComposeImages,
  isCompressibleImage,
  withTimeout,
} from "@/mail/compressImage";
import { htmlTextNewlinesToBreaks, isBlankHtml, stripHtml } from "@/mail/htmlBody";
import { applyMailLineSpacing } from "@/mail/prefs";
import { triggerFileInput } from "@/lib/triggerFileInput";
import {
  MAIL_SIGNATURE_EVENT,
  composeBodyIsBlank,
  insertMailSignature,
  readMailSignature,
  swapMailSignature,
} from "@/mail/signature";
import {
  RecipientField,
  RECIPIENT_DND_MIME,
  readRecipientDragPayload,
  type RecipientFieldId,
} from "@/components/RecipientField";
import {
  addRecipientChip,
  joinRecipientList,
  parseRecipientList,
} from "@/mail/recipientChips";

/** Drop editor focus + native selection UI before the discard dialog takes over. */
function releaseComposeFocus() {
  const active = document.activeElement;
  if (active instanceof HTMLElement) active.blur();
  const sel = window.getSelection();
  if (sel && sel.rangeCount) sel.removeAllRanges();
}

const MODE_LABEL: Record<ComposeDraft["mode"], string> = {
  new: "New Message",
  reply: "Reply",
  replyAll: "Reply All",
  forward: "Forward",
};

export type SentMailInfo = {
  mode: ComposeDraft["mode"];
  accountId: string;
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  bodyHtml: string;
  bodyText: string;
  /** Provider thread id when Hub still has one (same-mailbox reply). */
  threadId?: string;
  /** Open conversation key at compose time — used when threadId was cleared. */
  sourceThreadKey?: string;
  /** Server ids from POST /api/mail when the API returns them. */
  serverMessageId?: string;
  serverThreadId?: string;
};

export type SavedDraftInfo = {
  accountId: string;
  threadId?: string;
  messageId?: string;
  providerDraftId?: string;
  subject: string;
};

export type ScheduledSendInfo = {
  id: string;
  sendAt: string;
  accountId: string;
};

type Props = {
  accounts: MailAccount[];
  draft: ComposeDraft;
  onChange: (next: ComposeDraft) => void;
  onClose: () => void;
  onSent: (sent: SentMailInfo) => void;
  onSavedDraft?: (saved: SavedDraftInfo) => void;
  onScheduled?: (scheduled: ScheduledSendInfo) => void;
  accountLabel: (account: MailAccount) => string;
};

/** Per-file cap before base64 (~Gmail 25 MB message ceiling leaves headroom). */
const MAX_ATTACH_BYTES = 10 * 1024 * 1024;
/** Total raw attachment bytes across the draft (base64 inflates ~4/3). */
const MAX_ATTACH_TOTAL_BYTES = 18 * 1024 * 1024;
/**
 * Estimated JSON body ceiling many mail proxies reject around here.
 * Base64 length ≈ 4/3 of file bytes — stay under ~5.5 MB of base64.
 */
const WARN_BASE64_TOTAL = 5_500_000;
/** Auto-compress large JPEGs on attach. */
const AUTO_COMPRESS_BYTES = 1_200_000;
/**
 * PNG / HEIC screenshots are often already under the JPEG threshold but still
 * large as base64 — shrink these earlier so the first Send is smaller.
 */
const AUTO_COMPRESS_PNG_HEIC_BYTES = 180_000;
/** Keep the progress bar on screen long enough to see on fast devices. */
const MIN_PROGRESS_VISIBLE_MS = 600;
/** Auto-compress (incl. HEIC→JPEG) must not freeze the bar at 75% forever. */
const ATTACH_COMPRESS_BUDGET_MS = 15_000;

function isPngOrHeic(file: {
  filename?: string;
  mimeType?: string;
  name?: string;
  type?: string;
}): boolean {
  const mime = (file.mimeType || file.type || "").toLowerCase();
  const name = (file.filename || file.name || "").toLowerCase();
  return (
    mime.includes("png") ||
    mime.includes("heic") ||
    mime.includes("heif") ||
    /\.(png|heic|heif)$/.test(name)
  );
}

/** Photos we can still shrink (PNG/HEIC, or never compressed) for a flaky-network retry. */
function photosWorthCompressRetry(files: ComposeAttachment[]): boolean {
  return files.some((file) => {
    if (!isCompressibleImage(file) || !file.contentBase64) return false;
    if (isPngOrHeic(file)) return true;
    return !file.compressedFromSize;
  });
}

function autoCompressThreshold(file: {
  filename?: string;
  mimeType?: string;
  name?: string;
  type?: string;
}): number {
  return isPngOrHeic(file) ? AUTO_COMPRESS_PNG_HEIC_BYTES : AUTO_COMPRESS_BYTES;
}

type PendingUpload = {
  id: string;
  filename: string;
  size: number;
  /** 0–100 while reading/encoding in the browser. */
  progress: number;
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function yieldFrame(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** Nudge the bar from `from` toward `to` while a long HEIC/compress step runs. */
function startProgressHeartbeat(
  onProgress: (pct: number) => void,
  from: number,
  to: number,
  everyMs = 450,
): () => void {
  let current = from;
  const id = window.setInterval(() => {
    if (current >= to) return;
    current = Math.min(to, current + 2);
    onProgress(current);
  }, everyMs);
  return () => window.clearInterval(id);
}

/** Safe base64 via FileReader — avoids fromCharCode spread stack overflows on photos. */
function fileToBase64(file: File, onProgress: (pct: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.min(90, Math.round((event.loaded / event.total) * 90)));
      }
    };
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      const base64 = comma >= 0 ? result.slice(comma + 1) : result;
      onProgress(100);
      resolve(base64);
    };
    reader.onerror = () =>
      reject(
        new Error(
          humanizeClientError(
            reader.error,
            `Could not read "${file.name}". Try choosing the photo again.`,
          ),
        ),
      );
    onProgress(5);
    reader.readAsDataURL(file);
  });
}

function attachmentBase64Total(attachments: ComposeAttachment[]): number {
  return attachments.reduce((sum, file) => sum + (file.contentBase64?.length || 0), 0);
}

/** Decode draft base64 into a Blob for multipart upload (Gmail-style binary hop). */
function attachmentToBlob(file: ComposeAttachment): Blob {
  const base64 = file.contentBase64 || "";
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: file.mimeType || "application/octet-stream" });
}

type MailSendFields = {
  accountId: string;
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  bodyHtml?: string;
  threadId?: string;
  inReplyTo?: string;
  references?: string;
  attachments: ComposeAttachment[];
  draftMessageId?: string;
  providerDraftId?: string;
};

/** Tip 124: attachments ride multipart FormData (raw bytes) — avoids Express JSON ~100KB 413. */
function buildMailSendFormData(fields: MailSendFields): FormData {
  const form = new FormData();
  form.append("accountId", fields.accountId);
  form.append("to", fields.to);
  if (fields.cc) form.append("cc", fields.cc);
  if (fields.bcc) form.append("bcc", fields.bcc);
  form.append("subject", fields.subject);
  form.append("body", fields.body);
  if (fields.bodyHtml) form.append("bodyHtml", fields.bodyHtml);
  if (fields.threadId) form.append("threadId", fields.threadId);
  if (fields.inReplyTo) form.append("inReplyTo", fields.inReplyTo);
  if (fields.references) form.append("references", fields.references);
  if (fields.draftMessageId) form.append("draftMessageId", fields.draftMessageId);
  if (fields.providerDraftId) form.append("providerDraftId", fields.providerDraftId);
  // Meta helps APIs that only read filenames from a JSON field.
  form.append(
    "attachmentsMeta",
    JSON.stringify(
      fields.attachments.map((file) => ({
        filename: file.filename,
        mimeType: file.mimeType,
        size: file.size || undefined,
        contentId: file.contentId || undefined,
        inline: file.inline || undefined,
      })),
    ),
  );
  for (const file of fields.attachments) {
    form.append("attachments", attachmentToBlob(file), file.filename);
  }
  return form;
}

function buildMailSendJson(fields: MailSendFields): string {
  return JSON.stringify({
    accountId: fields.accountId,
    to: fields.to,
    cc: fields.cc || undefined,
    bcc: fields.bcc || undefined,
    subject: fields.subject,
    body: fields.body,
    bodyHtml: fields.bodyHtml,
    threadId: fields.threadId,
    inReplyTo: fields.inReplyTo,
    references: fields.references,
    draftMessageId: fields.draftMessageId,
    providerDraftId: fields.providerDraftId,
    attachments: fields.attachments.map(({ filename, mimeType, contentBase64, contentId, inline }) => ({
      filename,
      mimeType,
      contentBase64,
      ...(contentId ? { contentId } : {}),
      ...(inline ? { inline: true } : {}),
    })),
  });
}

function toDatetimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function defaultScheduleLocalValue(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 90);
  d.setSeconds(0, 0);
  return toDatetimeLocalValue(d);
}

function fromDatetimeLocalValue(local: string): string {
  return new Date(local).toISOString();
}

function formatScheduleLabel(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function guessFileMime(file: File): string {
  if (file.type) return file.type;
  const name = (file.name || "").toLowerCase();
  if (/\.heic$/i.test(name)) return "image/heic";
  if (/\.heif$/i.test(name)) return "image/heif";
  if (/\.png$/i.test(name)) return "image/png";
  if (/\.jpe?g$/i.test(name)) return "image/jpeg";
  if (/\.webp$/i.test(name)) return "image/webp";
  if (/\.gif$/i.test(name)) return "image/gif";
  return "application/octet-stream";
}

async function fileToAttachment(
  file: File,
  onProgress: (pct: number) => void,
): Promise<ComposeAttachment> {
  const mimeType = guessFileMime(file);
  if (file.size > MAX_ATTACH_BYTES && !isCompressibleImage({ filename: file.name, mimeType })) {
    throw new Error(
      `"${file.name}" is ${formatBytes(file.size)} — max ${formatBytes(MAX_ATTACH_BYTES)} per file.`,
    );
  }
  const started = Date.now();
  const contentBase64 = await fileToBase64(file, (pct) => onProgress(Math.min(70, pct)));
  let attachment: ComposeAttachment = {
    filename: file.name,
    mimeType,
    contentBase64,
    size: file.size,
  };

  // iPhone PNGs / HEIC / large JPEGs — shrink on attach before first Send.
  // Tip 125: HEIC compress failures must not block attach (decode via heic2any).
  // Tip 128: heic2any can hang forever at 75% — heartbeat + hard timeout, then keep original.
  if (isCompressibleImage(attachment) && file.size >= autoCompressThreshold({ ...file, mimeType })) {
    onProgress(75);
    const stopHeartbeat = startProgressHeartbeat(onProgress, 75, 92);
    try {
      const { attachments } = await withTimeout(
        compressComposeImages([attachment], {
          maxEdge: isPngOrHeic({ ...file, mimeType }) ? 1400 : 1600,
          quality: isPngOrHeic({ ...file, mimeType }) ? 0.68 : 0.72,
          targetBytes: isPngOrHeic({ ...file, mimeType }) ? 450_000 : 850_000,
        }),
        ATTACH_COMPRESS_BUDGET_MS,
        "Photo prepare timed out.",
      );
      const next = attachments[0]!;
      if (
        (next.size || file.size) < file.size ||
        (isPngOrHeic({ ...file, mimeType }) && next.mimeType === "image/jpeg")
      ) {
        attachment = {
          ...next,
          compressedFromSize: file.size,
        };
      }
    } catch {
      // Keep original bytes — user can still send (multipart handles ~350KB HEIC).
    } finally {
      stopHeartbeat();
    }
    onProgress(95);
  }

  const elapsed = Date.now() - started;
  if (elapsed < MIN_PROGRESS_VISIBLE_MS) {
    await sleep(MIN_PROGRESS_VISIBLE_MS - elapsed);
  }
  onProgress(100);
  await yieldFrame();
  return attachment;
}

function fromOptionLabel(account: MailAccount, fallback: (a: MailAccount) => string): string {
  const email = (account.email || "").trim();
  if (email) return email;
  return fallback(account);
}

export function MailComposePanel({
  accounts,
  draft,
  onChange,
  onClose,
  onSent,
  onSavedDraft,
  onScheduled,
  accountLabel,
}: Props) {
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [discardOpen, setDiscardOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState(() => defaultScheduleLocalValue());
  const [sendMenuOpen, setSendMenuOpen] = useState(false);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [attachWaitOpen, setAttachWaitOpen] = useState(false);
  const [sizeOfferOpen, setSizeOfferOpen] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [sendQueuedBanner, setSendQueuedBanner] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const keepEditingRef = useRef<HTMLButtonElement | null>(null);
  const attachWaitKeepRef = useRef<HTMLButtonElement | null>(null);
  const sizeOfferPrimaryRef = useRef<HTMLButtonElement | null>(null);
  const toInputRef = useRef<HTMLInputElement | null>(null);
  const sendMenuWrapRef = useRef<HTMLDivElement | null>(null);
  const draftRef = useRef(draft);
  const sendWhenReadyRef = useRef(false);
  const sendingRef = useRef(false);
  const pendingCountRef = useRef(0);
  const dragDepthRef = useRef(0);
  const attachInputId = "mail-compose-attach-input";

  draftRef.current = draft;
  pendingCountRef.current = pendingUploads.length;

  const uploading = pendingUploads.length > 0;
  const overallUploadPct = uploading
    ? Math.round(
        pendingUploads.reduce((sum, item) => sum + item.progress, 0) / pendingUploads.length,
      )
    : 0;

  useEffect(() => {
    if (!discardOpen) return;
    releaseComposeFocus();
    const id = window.requestAnimationFrame(() => {
      keepEditingRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [discardOpen]);

  useEffect(() => {
    if (!attachWaitOpen) return;
    releaseComposeFocus();
    const id = window.requestAnimationFrame(() => {
      attachWaitKeepRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [attachWaitOpen]);

  useEffect(() => {
    if (!sizeOfferOpen) return;
    releaseComposeFocus();
    const id = window.requestAnimationFrame(() => {
      sizeOfferPrimaryRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [sizeOfferOpen]);

  useEffect(() => {
    if (!sendMenuOpen) return;
    function onDoc(event: MouseEvent) {
      if (!sendMenuWrapRef.current?.contains(event.target as Node)) setSendMenuOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [sendMenuOpen]);

  function patch(partial: Partial<ComposeDraft>) {
    onChange({ ...draft, ...partial });
  }

  useEffect(() => {
    function onSignatures() {
      const current = draftRef.current;
      if (!current.accountId || current.body.includes('data-hub-signature="1"')) return;
      const use = current.mode === "new" ? "new" : "reply";
      const next = insertMailSignature(current.body, readMailSignature(current.accountId, use));
      if (next !== current.body) onChange({ ...current, body: next });
    }
    window.addEventListener(MAIL_SIGNATURE_EVENT, onSignatures);
    return () => window.removeEventListener(MAIL_SIGNATURE_EVENT, onSignatures);
  }, [onChange]);

  /** Tip 194: drag a To/Cc/Bcc chip onto another recipient row. */
  function moveRecipientChip(chip: string, from: RecipientFieldId, to: RecipientFieldId) {
    if (!chip.trim() || from === to) return;
    const current = draftRef.current;
    const fromKey = from;
    const toKey = to;
    const fromList = parseRecipientList(current[fromKey]).filter(
      (entry) => entry.toLowerCase() !== chip.toLowerCase(),
    );
    const toList = addRecipientChip(parseRecipientList(current[toKey]), chip);
    patch({
      [fromKey]: joinRecipientList(fromList),
      [toKey]: joinRecipientList(toList),
      ...(to === "cc" ? { showCc: true } : {}),
      ...(to === "bcc" ? { showBcc: true } : {}),
    } as Partial<ComposeDraft>);
  }

  function onRecipientDropButton(event: DragEvent<HTMLButtonElement>, to: RecipientFieldId) {
    event.preventDefault();
    const payload = readRecipientDragPayload(event.dataTransfer);
    if (!payload) return;
    moveRecipientChip(payload.chip, payload.from, to);
  }

  function updatePending(id: string, progress: number) {
    setPendingUploads((list) =>
      list.map((item) => (item.id === id ? { ...item, progress } : item)),
    );
  }

  function openSizeOffer(message: string) {
    setError(message);
    releaseComposeFocus();
    setSizeOfferOpen(true);
  }

  function attachmentsUnderEmailCaps(files: ComposeAttachment[]) {
    const rawTotal = files.reduce((sum, file) => sum + (file.size || 0), 0);
    const b64Total = attachmentBase64Total(files);
    return (
      rawTotal <= MAX_ATTACH_TOTAL_BYTES &&
      b64Total <= WARN_BASE64_TOTAL &&
      files.every((file) => (file.size || 0) <= MAX_ATTACH_BYTES)
    );
  }

  async function compressPhotosAndMaybeSend(andSend: boolean) {
    const current = draftRef.current;
    const imageCount = current.attachments.filter(isCompressibleImage).length;
    if (!imageCount) {
      setSizeOfferOpen(false);
      setError("No photos to compress. Remove a large file, or use ATW Storage when it launches.");
      return;
    }
    setCompressing(true);
    setError("");
    try {
      const before = attachmentBase64Total(current.attachments);
      const { attachments, compressedCount, savedBytes } = await compressComposeImages(
        current.attachments,
        { maxEdge: 1280, quality: 0.65, targetBytes: 650_000 },
      );
      const withMeta = attachments.map((file, index) => {
        const prev = current.attachments[index];
        if (!prev || !isCompressibleImage(file)) return file;
        const prevSize = prev.size || Math.round((prev.contentBase64.length * 3) / 4);
        const nextSize = file.size || prevSize;
        if (nextSize < prevSize) {
          return { ...file, compressedFromSize: prev.compressedFromSize || prevSize };
        }
        return file;
      });
      onChange({ ...draftRef.current, attachments: withMeta });
      draftRef.current = { ...draftRef.current, attachments: withMeta };
      setSizeOfferOpen(false);
      const after = attachmentBase64Total(withMeta);
      const afterBytes = withMeta.reduce((sum, file) => sum + (file.size || 0), 0);
      const underCap = attachmentsUnderEmailCaps(withMeta);

      // Already tiny (e.g. 73 KB + 80 KB) — never claim "couldn't shrink enough".
      if (underCap) {
        if (compressedCount > 0 && savedBytes > 0) {
          setInfo(
            `Compressed ${compressedCount} photo${compressedCount === 1 ? "" : "s"} (saved ${formatBytes(savedBytes)}).`,
          );
        } else if (!andSend) {
          setInfo("Photos are already small enough for email.");
        }
        if (andSend) {
          setInfo("");
          await performSend({ skipSizeOffer: true });
        }
        return;
      }

      // Still over Hub caps after compress — only then surface shrink failure.
      if (compressedCount === 0 || after >= before * 0.98) {
        setError(
          `Could not shrink these photos enough for email (${formatBytes(afterBytes)}). Remove one, or wait for ATW Storage (paid).`,
        );
        return;
      }
      setInfo(
        `Compressed ${compressedCount} photo${compressedCount === 1 ? "" : "s"} (saved ${formatBytes(savedBytes)}). Still large — remove a file or try send.`,
      );
      if (andSend) {
        setInfo("");
        await performSend({ skipSizeOffer: true });
      }
    } catch (err) {
      setError(humanizeClientError(err, "Could not compress photos."));
    } finally {
      setCompressing(false);
    }
  }

  function buildComposeSendFields(current: ComposeDraft): MailSendFields {
    const to = current.to.trim();
    const cc = current.cc.trim();
    const bcc = current.bcc.trim();
    const extracted = isBlankHtml(current.body)
      ? { html: "", inline: [] as ComposeAttachment[] }
      : extractInlineImagesFromHtml(current.body);
    const rawHtml = extracted.html && !isBlankHtml(extracted.html) ? extracted.html : undefined;
    const bodyHtml = rawHtml ? applyMailLineSpacing(htmlTextNewlinesToBreaks(rawHtml)) : undefined;
    const bodyText = stripHtml(bodyHtml || current.body);
    const attachments = [...current.attachments, ...extracted.inline];
    return {
      accountId: current.accountId,
      to,
      cc: cc || undefined,
      bcc: bcc || undefined,
      subject: current.subject,
      body: bodyText,
      bodyHtml,
      threadId: current.threadId,
      inReplyTo: current.inReplyTo,
      references: current.references,
      attachments,
      draftMessageId: current.sourceMessageId,
      providerDraftId: current.providerDraftId,
    };
  }

  async function postComposeAction<T>(
    path: string,
    fields: MailSendFields,
    extra?: Record<string, string>,
  ): Promise<T> {
    const timeoutMs = fields.attachments.length ? 120_000 : 45_000;
    const jsonBody = { ...JSON.parse(buildMailSendJson(fields)), ...extra };
    if (fields.attachments.length) {
      try {
        const form = buildMailSendFormData(fields);
        if (extra) {
          for (const [key, value] of Object.entries(extra)) form.append(key, value);
        }
        return await api<T>(path, { method: "POST", timeoutMs, body: form });
      } catch (multipartErr) {
        const multiStatus =
          multipartErr && typeof multipartErr === "object" && "status" in multipartErr
            ? Number(multipartErr.status)
            : 0;
        if (multiStatus === 415) {
          return await api<T>(path, {
            method: "POST",
            timeoutMs,
            body: JSON.stringify(jsonBody),
          });
        }
        throw multipartErr;
      }
    }
    return await api<T>(path, {
      method: "POST",
      timeoutMs,
      body: JSON.stringify(jsonBody),
    });
  }

  async function performSaveDraft(opts?: { closeAfter?: boolean }) {
    const current = draftRef.current;
    if (!current.accountId) {
      setError("Choose a From account.");
      return;
    }
    if (pendingCountRef.current > 0) {
      setError("Attachments are still loading. Wait for them to finish before saving.");
      return;
    }
    if (
      !current.to &&
      !current.subject &&
      composeBodyIsBlank(current.body) &&
      !current.attachments.length
    ) {
      if (opts?.closeAfter !== false) onClose();
      return;
    }
    setSavingDraft(true);
    setError("");
    setInfo("");
    try {
      const fields = buildComposeSendFields(current);
      const result = await postComposeAction<{
        id?: string;
        threadId?: string;
        messages?: { id?: string }[];
        providerDraftId?: string;
        subject?: string;
      }>("/api/mail/draft", fields);
      const messageId = result.messages?.[0]?.id;
      onSavedDraft?.({
        accountId: current.accountId,
        threadId: result.threadId || result.id,
        messageId,
        providerDraftId: result.providerDraftId,
        subject: result.subject || current.subject,
      });
      if (messageId || result.providerDraftId) {
        onChange({
          ...draftRef.current,
          sourceMessageId: messageId || draftRef.current.sourceMessageId,
          providerDraftId: result.providerDraftId || draftRef.current.providerDraftId,
        });
      }
      if (opts?.closeAfter !== false) {
        setDiscardOpen(false);
        onClose();
      } else {
        setInfo("Draft saved to your Drafts folder.");
      }
    } catch (err) {
      setError(humanizeClientError(err, "Could not save draft."));
    } finally {
      setSavingDraft(false);
    }
  }

  async function performScheduleSend() {
    const current = draftRef.current;
    if (!current.accountId) {
      setError("Choose a From account.");
      return;
    }
    if (!current.to.trim()) {
      setError("Add at least one To recipient before scheduling.");
      toInputRef.current?.focus();
      return;
    }
    if (!current.subject.trim()) {
      setError("Add a subject before scheduling.");
      return;
    }
    if (composeBodyIsBlank(current.body)) {
      setError("Write a message before scheduling.");
      return;
    }
    if (pendingCountRef.current > 0) {
      setError("Attachments are still loading. Wait for them to finish before scheduling.");
      return;
    }
    const sendAt = fromDatetimeLocalValue(scheduleAt);
    setScheduling(true);
    setError("");
    setInfo("");
    try {
      const fields = buildComposeSendFields(current);
      const result = await postComposeAction<{ id: string; sendAt: string; accountId: string }>(
        "/api/mail/schedule",
        fields,
        { sendAt },
      );
      setScheduleOpen(false);
      setInfo(
        `Send scheduled for ${formatScheduleLabel(result.sendAt)}. Hub sends it from the server at that time.`,
      );
      onScheduled?.({
        id: result.id,
        sendAt: result.sendAt,
        accountId: result.accountId,
      });
      window.setTimeout(() => onClose(), 900);
    } catch (err) {
      setError(humanizeClientError(err, "Could not schedule send."));
    } finally {
      setScheduling(false);
    }
  }

  function closeSendMenu() {
    setSendMenuOpen(false);
  }

  function openScheduleSheet() {
    closeSendMenu();
    releaseComposeFocus();
    setScheduleAt(defaultScheduleLocalValue());
    setScheduleOpen(true);
  }

  function confirmDiscardWithoutSave() {
    setDiscardOpen(false);
    closeSendMenu();
    onClose();
  }

  async function performSend(opts?: {
    skipSizeOffer?: boolean;
    /** Already did one compress+retry after a reachability failure. */
    skipNetworkCompressRetry?: boolean;
  }) {
    const current = draftRef.current;
    if (!current.accountId) {
      setError("Choose a From account.");
      return;
    }
    if (!current.to.trim()) {
      setError("Add at least one To recipient before sending.");
      toInputRef.current?.focus();
      return;
    }
    if (pendingCountRef.current > 0) {
      // Still encoding — queue instead of failing silently.
      sendWhenReadyRef.current = true;
      setSendQueuedBanner(true);
      setError("");
      return;
    }
    const attachTotal = current.attachments.reduce((sum, file) => sum + (file.size || 0), 0);
    if (attachTotal > MAX_ATTACH_TOTAL_BYTES) {
      openSizeOffer(
        `Attachments are too large (${formatBytes(attachTotal)}). Compress photos, or remove a file.`,
      );
      return;
    }
    const base64Total = attachmentBase64Total(current.attachments);
    if (
      !opts?.skipSizeOffer &&
      base64Total > WARN_BASE64_TOTAL &&
      current.attachments.some(isCompressibleImage)
    ) {
      openSizeOffer(
        "These photos are large for email. Compress them to send, or remove a file.",
      );
      return;
    }
    if (sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setSendQueuedBanner(false);
    setError("");
    setInfo("");
    const to = current.to.trim();
    const cc = current.cc.trim();
    const bcc = current.bcc.trim();
    // Tip 193: promote in-body data: images to cid: inline MIME parts (Outlook-style).
    const extracted = isBlankHtml(current.body)
      ? { html: "", inline: [] as ComposeAttachment[] }
      : extractInlineImagesFromHtml(current.body);
    const rawHtml = extracted.html && !isBlankHtml(extracted.html) ? extracted.html : undefined;
    // Pasted plain text (a Zoom invite) keeps its newlines in the editor, and
    // HTML mail would otherwise draw that as one line.
    const bodyHtml = rawHtml ? applyMailLineSpacing(htmlTextNewlinesToBreaks(rawHtml)) : undefined;
    const bodyText = stripHtml(bodyHtml || current.body);
    const attachments = [...current.attachments, ...extracted.inline];
    const sendFields: MailSendFields = {
      accountId: current.accountId,
      to,
      cc: cc || undefined,
      bcc: bcc || undefined,
      subject: current.subject,
      body: bodyText,
      bodyHtml,
      threadId: current.threadId,
      inReplyTo: current.inReplyTo,
      references: current.references,
      attachments,
    };
    try {
      const timeoutMs = attachments.length ? 120_000 : 45_000;
      let result: { id?: string; threadId?: string; messageId?: string };
      if (attachments.length) {
        // Tip 124: multipart first (raw bytes bypass Express JSON ~100KB). Live probe:
        // ~250KB multipart → 401 (accepted); same size JSON → 413.
        try {
          result = await api<{ id?: string; threadId?: string; messageId?: string }>("/api/mail", {
            method: "POST",
            timeoutMs,
            body: buildMailSendFormData(sendFields),
          });
        } catch (multipartErr) {
          const multiStatus =
            multipartErr && typeof multipartErr === "object" && "status" in multipartErr
              ? Number(multipartErr.status)
              : 0;
          // Old API without multipart — fall back to JSON only on unsupported media type.
          // Do not fall back on 400/500: empty multipart parse used to JSON-encode attachments and hit 413.
          if (multiStatus === 415) {
            result = await api<{ id?: string; threadId?: string; messageId?: string }>("/api/mail", {
              method: "POST",
              timeoutMs,
              body: buildMailSendJson(sendFields),
            });
          } else {
            throw multipartErr;
          }
        }
      } else {
        result = await api<{ id?: string; threadId?: string; messageId?: string }>("/api/mail", {
          method: "POST",
          timeoutMs,
          body: buildMailSendJson(sendFields),
        });
      }
      onSent({
        mode: current.mode,
        accountId: current.accountId,
        to,
        cc,
        bcc,
        subject: current.subject,
        bodyHtml: bodyHtml || "",
        bodyText,
        threadId: current.threadId,
        sourceThreadKey: current.sourceThreadKey,
        serverMessageId: result?.id || result?.messageId,
        serverThreadId: result?.threadId,
      });
    } catch (err) {
      const status = err && typeof err === "object" && "status" in err ? Number(err.status) : 0;
      const unreachable = status === 0 && isUnreachableClientError(err);
      if (status === 413) {
        const rawTotal = current.attachments.reduce((sum, file) => sum + (file.size || 0), 0);
        const b64Total = attachmentBase64Total(current.attachments);
        // Tiny payload already — don't pretend another compress pass will help.
        if (attachmentsUnderEmailCaps(current.attachments)) {
          setError(
            `Mail gateway rejected the upload (413) for ~${formatBytes(rawTotal)} of files (~${formatBytes(b64Total)} if JSON-encoded). Try again on Wi‑Fi, or remove a file. Hub now sends photos as multipart like Gmail — if this keeps failing the API body limit still needs a raise.`,
          );
        } else if (current.attachments.some(isCompressibleImage)) {
          openSizeOffer(
            "Attachment too large for the mail server. Compress photos to try again, or remove a file.",
          );
        } else {
          setError(
            `Attachment too large for the mail server (${formatBytes(rawTotal)}). Remove a file, or wait for ATW Storage (paid).`,
          );
        }
      } else if (status === 408) {
        setError("Send timed out with attachments. Try compressing photos or sending fewer files.");
      } else if (
        unreachable &&
        !opts?.skipNetworkCompressRetry &&
        photosWorthCompressRetry(current.attachments)
      ) {
        // Tip 120: reachability fail with PNG/uncompressed photos — shrink once and resend.
        // Size was never the stated error; smaller payload just helps flaky mobile uploads.
        sendingRef.current = false;
        setSending(false);
        setInfo("Send failed — compressing photos and retrying…");
        setError("");
        setCompressing(true);
        try {
          const before = attachmentBase64Total(current.attachments);
          const { attachments, compressedCount, savedBytes } = await compressComposeImages(
            current.attachments,
            { maxEdge: 1280, quality: 0.65, targetBytes: 450_000 },
          );
          const withMeta = attachments.map((file, index) => {
            const prev = current.attachments[index];
            if (!prev || !isCompressibleImage(file)) return file;
            const prevSize = prev.size || Math.round((prev.contentBase64.length * 3) / 4);
            const nextSize = file.size || prevSize;
            if (nextSize < prevSize) {
              return { ...file, compressedFromSize: prev.compressedFromSize || prevSize };
            }
            return file;
          });
          onChange({ ...draftRef.current, attachments: withMeta });
          draftRef.current = { ...draftRef.current, attachments: withMeta };
          const after = attachmentBase64Total(withMeta);
          if (compressedCount > 0 && savedBytes > 0 && after < before) {
            setInfo(
              `Compressed ${compressedCount} photo${compressedCount === 1 ? "" : "s"} (saved ${formatBytes(savedBytes)}). Retrying send…`,
            );
          } else {
            setInfo("Retrying send…");
          }
          setCompressing(false);
          await performSend({ skipSizeOffer: true, skipNetworkCompressRetry: true });
        } catch (compressErr) {
          setCompressing(false);
          setInfo("");
          setError(
            `${humanizeClientError(err, "Could not reach the mail server.")} Tap Send to try again.`,
          );
          void compressErr;
        }
        return;
      } else if (unreachable) {
        setError(
          "Could not reach the mail server. Check your connection and tap Send again.",
        );
      } else {
        setError(humanizeClientError(err, "Could not send."));
      }
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  // Queued send: fire once every pending encode finishes.
  useEffect(() => {
    if (uploading || !sendWhenReadyRef.current || sendingRef.current) return;
    sendWhenReadyRef.current = false;
    setSendQueuedBanner(false);
    void performSend();
  }, [uploading]);

  function onComposeDragEnter(event: DragEvent<HTMLElement>) {
    if (sending) return;
    if (![...event.dataTransfer.types].includes("Files")) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setDragOver(true);
  }

  function onComposeDragOver(event: DragEvent<HTMLElement>) {
    if (sending) return;
    if (![...event.dataTransfer.types].includes("Files")) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  }

  function onComposeDragLeave(event: DragEvent<HTMLElement>) {
    event.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragOver(false);
  }

  function onComposeDrop(event: DragEvent<HTMLElement>) {
    event.preventDefault();
    dragDepthRef.current = 0;
    setDragOver(false);
    if (sending) return;
    void onPickFiles(event.dataTransfer.files);
  }

  async function onPickFiles(files: FileList | null) {
    if (!files?.length) return;
    setError("");
    setInfo("");
    const picked = Array.from(files);
    const existing = [...draftRef.current.attachments];
    const existingTotal = existing.reduce((sum, file) => sum + (file.size || 0), 0);
    const batchBytes = picked.reduce((sum, file) => sum + file.size, 0);
    if (existingTotal + batchBytes > MAX_ATTACH_TOTAL_BYTES) {
      setError(
        `Attachments would exceed ${formatBytes(MAX_ATTACH_TOTAL_BYTES)} total. Remove a file or send a smaller one.`,
      );
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    const batch: PendingUpload[] = picked.map((file, index) => ({
      id: `${Date.now()}-${index}-${file.name}`,
      filename: file.name || `photo-${index + 1}`,
      size: file.size,
      progress: 2,
    }));
    // Paint the bar before any await so the user always sees progress.
    setPendingUploads((list) => [...list, ...batch]);
    await yieldFrame();

    try {
      // Tip 128: encode in parallel so one stuck HEIC does not freeze the other at 2%.
      // Each file still has its own compress timeout (see fileToAttachment).
      const settled = await Promise.all(
        picked.map(async (file, i) => {
          const pending = batch[i]!;
          try {
            const encoded = await fileToAttachment(file, (pct) =>
              updatePending(pending.id, pct),
            );
            return { pending, encoded, ok: true as const };
          } catch (err) {
            return { pending, err, ok: false as const };
          }
        }),
      );

      const ready = [...existing];
      let failed: unknown;
      for (const item of settled) {
        setPendingUploads((list) => list.filter((row) => row.id !== item.pending.id));
        if (!item.ok) {
          failed = item.err;
          continue;
        }
        ready.push(item.encoded);
        onChange({ ...draftRef.current, attachments: [...ready] });
        await yieldFrame();
      }
      if (failed && ready.length === existing.length) {
        throw failed instanceof Error ? failed : new Error("Could not attach file.");
      }
      if (failed) {
        setError(
          humanizeClientError(
            failed,
            "Some photos could not be attached. The rest are ready to send.",
          ),
        );
      }
    } catch (err) {
      setPendingUploads([]);
      sendWhenReadyRef.current = false;
      setSendQueuedBanner(false);
      setError(humanizeClientError(err, "Could not attach file. Try choosing the photos again."));
    }
    if (fileRef.current) fileRef.current.value = "";
  }

  function queueSendWhenReady() {
    sendWhenReadyRef.current = true;
    setSendQueuedBanner(true);
    setAttachWaitOpen(false);
    setError("");
    // If encoding already finished while the dialog was open, send now.
    if (pendingCountRef.current === 0) {
      sendWhenReadyRef.current = false;
      void performSend();
    }
  }

  async function send(event: FormEvent) {
    event.preventDefault();
    if (!draft.accountId) {
      setError("Choose a From account.");
      return;
    }
    if (!draft.to.trim()) {
      setError("Add at least one To recipient before sending.");
      toInputRef.current?.focus();
      return;
    }
    if (uploading) {
      releaseComposeFocus();
      setAttachWaitOpen(true);
      return;
    }
    await performSend();
  }

  function requestClose() {
    if (uploading || sendQueuedBanner || sending) {
      releaseComposeFocus();
      setError(
        sending
          ? "Still sending — keep Hub open until it finishes."
          : sendQueuedBanner
            ? "Send is queued — keep Hub open, or cancel Wait & send first."
            : "Attachments are still loading. Wait for them to finish, or remove them before closing.",
      );
      return;
    }
    if (draft.to || draft.subject || !composeBodyIsBlank(draft.body) || draft.attachments.length) {
      releaseComposeFocus();
      setDiscardOpen(true);
      return;
    }
    onClose();
  }

  const editorKey = `${draft.mode}:${draft.sourceMessageId || "new"}:${draft.accountId}`;
  const showProgress = uploading || sendQueuedBanner || sending || compressing;
  const composeBusy = sending || savingDraft || scheduling;
  const dialogOpen = discardOpen || attachWaitOpen || sizeOfferOpen || scheduleOpen;
  const hasCompressible = draft.attachments.some(isCompressibleImage);
  const attachCount = draft.attachments.length + pendingUploads.length;
  const showAttachList = pendingUploads.length > 0 || draft.attachments.length > 0;

  return (
    <div className="mail-compose-overlay" role="dialog" aria-modal="true" aria-label={MODE_LABEL[draft.mode]}>
      <form
        className={`mail-compose-sheet${dialogOpen ? " is-discarding" : ""}`}
        onSubmit={(e) => void send(e)}
        inert={dialogOpen || undefined}
      >
        <header className="mail-compose-toolbar">
          <button type="button" className="icon-btn" aria-label="Close" title="Close" onClick={requestClose}>
            <X size={20} />
          </button>
          <div className="mail-compose-toolbar-right">
            {draft.mode === "reply" || draft.mode === "replyAll" ? (
              <div className="mail-compose-mode-toggle" role="group" aria-label="Reply mode">
                <button
                  type="button"
                  className={draft.mode === "reply" ? "active" : ""}
                  aria-pressed={draft.mode === "reply"}
                  onClick={() => onChange(applyReplyMode(draft, "reply"))}
                >
                  Reply
                </button>
                <button
                  type="button"
                  className={draft.mode === "replyAll" ? "active" : ""}
                  aria-pressed={draft.mode === "replyAll"}
                  onClick={() => onChange(applyReplyMode(draft, "replyAll"))}
                >
                  Reply all
                </button>
              </div>
            ) : (
              <span className="mail-compose-toolbar-title">{MODE_LABEL[draft.mode]}</span>
            )}
            <button
              type="button"
              className="icon-btn"
              aria-label="AI coming soon"
              title="AI — Coming soon"
              disabled
            >
              <Sparkles size={18} />
            </button>
            <button
              type="button"
              className={`icon-btn mail-compose-attach-trigger${uploading ? " is-busy" : ""}${
                sending ? " is-disabled" : ""
              }`}
              disabled={sending}
              aria-label={
                uploading
                  ? `Attaching files, ${overallUploadPct}%`
                  : attachCount
                    ? `Attach file, ${attachCount} attached`
                    : "Attach file"
              }
              title={uploading ? `Attaching… ${overallUploadPct}%` : "Attach"}
              aria-busy={uploading || undefined}
              onClick={() => triggerFileInput(fileRef.current)}
            >
              <Paperclip size={18} />
              {attachCount > 0 ? (
                <span className="mail-compose-attach-badge" aria-hidden>
                  {attachCount > 9 ? "9+" : attachCount}
                </span>
              ) : null}
            </button>
            <div className="mail-compose-send-split-wrap" ref={sendMenuWrapRef}>
              <div className="mail-compose-send-split">
                <button
                  type="submit"
                  className="mail-compose-send mail-compose-send-main"
                  disabled={composeBusy}
                  aria-label="Send"
                  title={
                    !draft.to.trim()
                      ? "Add a To recipient"
                      : uploading
                        ? "Attachments still loading"
                        : "Send"
                  }
                >
                  <Send size={16} />
                  <span>
                    {sending
                      ? "Sending…"
                      : sendQueuedBanner
                        ? "Queued…"
                        : uploading
                          ? "Wait…"
                          : "Send"}
                  </span>
                </button>
                <button
                  type="button"
                  className="mail-compose-send-menu-trigger"
                  disabled={composeBusy}
                  aria-label="More send options"
                  aria-haspopup="menu"
                  aria-expanded={sendMenuOpen}
                  title="More send options"
                  onClick={() => setSendMenuOpen((open) => !open)}
                >
                  <ChevronDown size={16} aria-hidden />
                </button>
              </div>
              {sendMenuOpen ? (
                <div className="mail-more-menu mail-compose-send-menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    disabled={composeBusy}
                    onClick={() => {
                      closeSendMenu();
                      void performSend();
                    }}
                  >
                    Send
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={composeBusy}
                    onClick={openScheduleSheet}
                  >
                    Send later
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    disabled={composeBusy || savingDraft}
                    onClick={() => {
                      closeSendMenu();
                      void performSaveDraft();
                    }}
                  >
                    {savingDraft ? "Saving draft…" : "Draft"}
                  </button>
                  <div className="mail-compose-menu-sep" role="separator" />
                  <button
                    type="button"
                    role="menuitem"
                    className="mail-compose-menu-danger"
                    disabled={composeBusy}
                    onClick={() => {
                      closeSendMenu();
                      confirmDiscardWithoutSave();
                    }}
                  >
                    Discard
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        {error && !sizeOfferOpen ? (
          <p className="mail-compose-error mail-compose-error-top" role="alert">
            {error}
          </p>
        ) : null}
        {info && !error ? (
          <p className="mail-compose-info mail-compose-error-top" role="status">
            {info}
          </p>
        ) : null}

        <div className="mail-compose-headers">
          <div className="mail-compose-row">
            <span className="mail-compose-key">From</span>
            <select
              className="mail-compose-control"
              aria-label="From"
              value={draft.accountId}
              onChange={(e) => {
                const accountId = e.target.value;
                const sameMailbox =
                  Boolean(accountId) &&
                  Boolean(draft.sourceAccountId) &&
                  accountId === draft.sourceAccountId;
                const signatureUse = draft.mode === "new" ? "new" : "reply";
                patch({
                  accountId,
                  threadId:
                    draft.mode === "new" || sameMailbox || !draft.sourceAccountId
                      ? draft.threadId
                      : undefined,
                  body: swapMailSignature(
                    draft.body,
                    readMailSignature(draft.accountId, signatureUse),
                    readMailSignature(accountId, signatureUse),
                  ),
                });
              }}
            >
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {fromOptionLabel(account, accountLabel)}
                </option>
              ))}
            </select>
          </div>

          <div className="mail-compose-row mail-compose-row-recipients">
            <span className="mail-compose-key">To</span>
            <RecipientField
              label="To"
              fieldId="to"
              value={draft.to}
              onChange={(to) => patch({ to })}
              onReceiveChip={(chip, from) => moveRecipientChip(chip, from, "to")}
              placeholder="name@company.com"
              inputRef={toInputRef}
              trailing={
                <>
                  {!draft.showCc ? (
                    <button
                      type="button"
                      className="mail-compose-chip-btn"
                      title="Show Cc, or drop a recipient here"
                      onClick={() => patch({ showCc: true })}
                      onDragOver={(event) => {
                        const types = [...event.dataTransfer.types];
                        if (types.includes(RECIPIENT_DND_MIME) || types.includes("text/plain")) {
                          event.preventDefault();
                          event.dataTransfer.dropEffect = "move";
                        }
                      }}
                      onDrop={(event) => onRecipientDropButton(event, "cc")}
                    >
                      Cc
                    </button>
                  ) : null}
                  {!draft.showBcc ? (
                    <button
                      type="button"
                      className="mail-compose-chip-btn"
                      title="Show Bcc, or drop a recipient here"
                      onClick={() => patch({ showBcc: true })}
                      onDragOver={(event) => {
                        const types = [...event.dataTransfer.types];
                        if (types.includes(RECIPIENT_DND_MIME) || types.includes("text/plain")) {
                          event.preventDefault();
                          event.dataTransfer.dropEffect = "move";
                        }
                      }}
                      onDrop={(event) => onRecipientDropButton(event, "bcc")}
                    >
                      Bcc
                    </button>
                  ) : null}
                </>
              }
            />
          </div>

          {draft.showCc ? (
            <div className="mail-compose-row mail-compose-row-recipients">
              <span className="mail-compose-key">Cc</span>
              <RecipientField
                label="Cc"
                fieldId="cc"
                value={draft.cc}
                onChange={(cc) => patch({ cc })}
                onReceiveChip={(chip, from) => moveRecipientChip(chip, from, "cc")}
                placeholder="cc@company.com"
              />
            </div>
          ) : null}

          {draft.showBcc ? (
            <div className="mail-compose-row mail-compose-row-recipients">
              <span className="mail-compose-key">Bcc</span>
              <RecipientField
                label="Bcc"
                fieldId="bcc"
                value={draft.bcc}
                onChange={(bcc) => patch({ bcc })}
                onReceiveChip={(chip, from) => moveRecipientChip(chip, from, "bcc")}
                placeholder="bcc@company.com"
              />
            </div>
          ) : null}

          <div className="mail-compose-row">
            <span className="mail-compose-key">Subject</span>
            <input
              className="mail-compose-control"
              type="text"
              aria-label="Subject"
              value={draft.subject}
              onChange={(e) => patch({ subject: e.target.value })}
              placeholder="Subject"
            />
          </div>
        </div>

        <div
          className={`mail-compose-body-wrap${dragOver ? " is-drop-target" : ""}`}
          onDragEnter={onComposeDragEnter}
          onDragOver={onComposeDragOver}
          onDragLeave={onComposeDragLeave}
          onDrop={onComposeDrop}
        >
          <div className={`mail-compose-attach-block${dragOver ? " is-drop-target" : ""}`}>
            {showProgress ? (
              <div
                className="mail-compose-upload-bar"
                role="status"
                aria-live="polite"
                aria-busy={uploading || sending}
              >
                <div className="mail-compose-upload-bar-label">
                  {compressing
                    ? "Compressing photos for email…"
                    : sending
                      ? "Sending message with attachments…"
                      : sendQueuedBanner
                        ? "Will send when attachments finish. Keep Hub open."
                        : pendingUploads.length === 1
                          ? `Preparing ${pendingUploads[0]!.filename}…`
                          : `Preparing ${pendingUploads.length} attachments…`}
                  {uploading ? <span>{overallUploadPct}%</span> : null}
                </div>
                <div
                  className={`mail-compose-upload-track${(sending || compressing) && !uploading ? " is-indeterminate" : ""}`}
                >
                  <div
                    className="mail-compose-upload-fill"
                    style={
                      uploading
                        ? { width: `${Math.max(overallUploadPct, 4)}%` }
                        : sending
                          ? undefined
                          : { width: sendQueuedBanner ? `${Math.max(overallUploadPct, 12)}%` : "0%" }
                    }
                  />
                </div>
                {sendQueuedBanner && !sending ? (
                  <button
                    type="button"
                    className="text-btn mail-compose-upload-cancel"
                    onClick={() => {
                      sendWhenReadyRef.current = false;
                      setSendQueuedBanner(false);
                    }}
                  >
                    Cancel queued send
                  </button>
                ) : null}
              </div>
            ) : null}

            {showAttachList ? (
              <ul className="mail-compose-attachments" aria-label="Attachments">
                {pendingUploads.map((file) => (
                  <li key={file.id} className="is-uploading">
                    <Paperclip size={14} aria-hidden />
                    <span className="mail-compose-attach-name">
                      {file.filename}
                      <em>
                        {formatBytes(file.size)} · preparing {file.progress}%
                      </em>
                    </span>
                    <div className="mail-compose-attach-mini" aria-hidden>
                      <div style={{ width: `${Math.max(file.progress, 4)}%` }} />
                    </div>
                  </li>
                ))}
                {draft.attachments.map((file, index) => (
                  <li key={`${file.filename}-${index}`}>
                    <Paperclip size={14} aria-hidden />
                    <span className="mail-compose-attach-name">
                      {file.filename}
                      <em>
                        {file.size ? formatBytes(file.size) : ""}
                        {file.compressedFromSize
                          ? ` · compressed from ${formatBytes(file.compressedFromSize)}`
                          : ""}
                      </em>
                    </span>
                    <button
                      type="button"
                      className="text-btn"
                      disabled={sending || uploading || compressing}
                      onClick={() =>
                        patch({
                          attachments: draft.attachments.filter((_, i) => i !== index),
                        })
                      }
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <button
                type="button"
                className={`mail-compose-attach-empty${sending ? " is-disabled" : ""}`}
                disabled={sending}
                onClick={() => triggerFileInput(fileRef.current)}
              >
                <Paperclip size={14} aria-hidden />
                <span>{dragOver ? "Drop files to attach" : "Attach files or drop them here"}</span>
              </button>
            )}
          </div>

          <MailComposeEditor
            key={editorKey}
            value={draft.body}
            onChange={(html) => patch({ body: html })}
            placeholder="Write your message…"
            locked={dialogOpen}
          />
        </div>

        <input
          id={attachInputId}
          ref={fileRef}
          type="file"
          multiple
          tabIndex={-1}
          aria-hidden="true"
          className="hub-file-input-offscreen"
          disabled={sending}
          onChange={(e) => void onPickFiles(e.target.files)}
        />
      </form>

      {attachWaitOpen ? (
        <div className="mail-compose-discard-scrim" role="presentation">
          <div
            className="mail-compose-discard"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="mail-attach-wait-title"
          >
            <h3 id="mail-attach-wait-title">Attachment still loading</h3>
            <p className="muted">
              Your file is still preparing. Wait and keep editing, or send automatically when it
              finishes. Do not close Hub until then.
            </p>
            <div className="mail-compose-discard-actions">
              <button
                ref={attachWaitKeepRef}
                type="button"
                className="text-btn"
                onClick={() => {
                  sendWhenReadyRef.current = false;
                  setSendQueuedBanner(false);
                  setAttachWaitOpen(false);
                }}
              >
                Wait
              </button>
              <button type="button" className="primary-btn" onClick={queueSendWhenReady}>
                Wait &amp; send
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {sizeOfferOpen ? (
        <div className="mail-compose-discard-scrim" role="presentation">
          <div
            className="mail-compose-discard"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="mail-size-offer-title"
          >
            <h3 id="mail-size-offer-title">Photos too large for email</h3>
            <p className="muted">
              {error ||
                "Mail servers reject large attachments. Compress photos here to send now."}
            </p>
            <p className="muted mail-compose-size-note">
              Full-size files via <strong>ATW Storage</strong> (cloud links) will be part of paid
              membership — not available yet.
            </p>
            <div className="mail-compose-discard-actions mail-compose-size-actions">
              <button
                type="button"
                className="text-btn"
                disabled={compressing}
                onClick={() => {
                  setSizeOfferOpen(false);
                }}
              >
                Keep editing
              </button>
              {hasCompressible ? (
                <>
                  <button
                    type="button"
                    className="text-btn"
                    disabled={compressing}
                    onClick={() => void compressPhotosAndMaybeSend(false)}
                  >
                    Compress only
                  </button>
                  <button
                    ref={sizeOfferPrimaryRef}
                    type="button"
                    className="primary-btn"
                    disabled={compressing}
                    onClick={() => void compressPhotosAndMaybeSend(true)}
                  >
                    {compressing ? "Compressing…" : "Compress & send"}
                  </button>
                </>
              ) : (
                <button
                  ref={sizeOfferPrimaryRef}
                  type="button"
                  className="primary-btn"
                  onClick={() => setSizeOfferOpen(false)}
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {discardOpen ? (
        <div className="mail-compose-discard-scrim" role="presentation">
          <div
            className="mail-compose-discard"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="mail-discard-title"
          >
            <h3 id="mail-discard-title">Discard this message?</h3>
            <p className="muted">
              Save a draft to your Drafts folder, keep editing, or discard without saving.
            </p>
            <div className="mail-compose-discard-actions mail-compose-discard-actions-stack">
              <button
                ref={keepEditingRef}
                type="button"
                className="text-btn mail-compose-discard-keep"
                onClick={() => setDiscardOpen(false)}
              >
                Keep editing
              </button>
              <button
                type="button"
                className="secondary-btn mail-compose-discard-save"
                disabled={savingDraft}
                onClick={() => void performSaveDraft()}
              >
                {savingDraft ? "Saving…" : "Save as draft"}
              </button>
              <button
                type="button"
                className="mail-compose-discard-danger"
                onClick={confirmDiscardWithoutSave}
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {scheduleOpen ? (
        <div className="mail-compose-discard-scrim" role="presentation">
          <div
            className="mail-compose-discard mail-compose-schedule"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mail-schedule-title"
          >
            <h3 id="mail-schedule-title">Send later</h3>
            <p className="muted">
              Hub stores the message and sends it from the server at the time you pick. Gmail
              scheduled send is not used.
            </p>
            <label className="mail-compose-schedule-field">
              <span className="mail-compose-key">Send at</span>
              <input
                className="mail-compose-control"
                type="datetime-local"
                value={scheduleAt}
                min={toDatetimeLocalValue(new Date(Date.now() + 60_000))}
                onChange={(e) => setScheduleAt(e.target.value)}
              />
            </label>
            <div className="mail-compose-discard-actions">
              <button
                type="button"
                className="text-btn"
                disabled={scheduling}
                onClick={() => setScheduleOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn"
                disabled={scheduling}
                onClick={() => void performScheduleSend()}
              >
                {scheduling ? "Scheduling…" : "Schedule send"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
